import * as React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FullPageSpinner from "components/common/FullPageSpinner";
import { loadContextPermissions } from "../../utils/users";

const permissionCache = new Map<string, string[]>();
const inflightRequests = new Map<string, Promise<string[] | undefined>>();

interface PermissionContextCheckProps {
  children: React.ReactElement;
  category: string;
  target?: string;
  requiredPermission?: string;
}

const PermissionContextCheck: React.FC<PermissionContextCheckProps> = ({
  children,
  category,
  target,
  requiredPermission,
}: PermissionContextCheckProps) => {
  const { identifier } = useParams();
  const resolvedTarget = target ?? identifier;
  const [loaded, setLoaded] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);

  useEffect(() => {
    const cacheKey = `${category}:${resolvedTarget}:${requiredPermission}`;

    if (permissionCache.has(cacheKey)) {
      setPermissions(permissionCache.get(cacheKey)!);
      setLoaded(true);
      return;
    }

    if (inflightRequests.has(cacheKey)) {
      inflightRequests
        .get(cacheKey)!
        .then((contextPermissions) => {
          if (contextPermissions) {
            setPermissions(contextPermissions);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setLoaded(true);
        });
      return;
    }

    const loadPermissions = async () => {
      try {
        const contextPermissions = await loadContextPermissions({
          category,
          target: resolvedTarget,
          requiredPermission,
        });
        if (contextPermissions) {
          permissionCache.set(cacheKey, contextPermissions);
          return contextPermissions;
        }
      } catch (e) {
        console.error(e);
        throw e;
      }
      return undefined;
    };

    const requestPromise = loadPermissions();
    inflightRequests.set(cacheKey, requestPromise);

    requestPromise
      .then((contextPermissions) => {
        if (contextPermissions) {
          setPermissions(contextPermissions);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setLoaded(true);
        inflightRequests.delete(cacheKey);
      });
  }, [category, resolvedTarget, requiredPermission]);

  if (!loaded) {
    return <FullPageSpinner />;
  }

  if (requiredPermission && !permissions.includes(requiredPermission)) {
    return <></>;
  }

  return <>{React.cloneElement(children, { permissions })}</>;
};

export default PermissionContextCheck;
